import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, orderBy, sumBy } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
// Models
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
// Utils
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getOptionPrice } from 'client/site-modules/shared/utils/inventory/get-option-price';
import { getPlural } from 'client/utils/plural';
// Constants
import { TrackingConstant } from 'client/tracking/constant';
// Components
import { UpgradeItem } from 'site-modules/inventory/components/features-and-specs/upgrade-item';

import './installed-upgrades.scss';

const OPTIONS_COLLAPSE_NUMBER = 6;

function isDescriptionEnabled({ index, optionsCount }) {
  return index !== OPTIONS_COLLAPSE_NUMBER - 1 && index !== optionsCount - 1;
}

export function InstalledUpgradesUI({
  vinData = {},
  optionPrices = {},
  isExpanded = false,
  toggleModal = null,
  summaryHeadingTag: SummaryHeadingTag = 'h3',
}) {
  const options = get(vinData, 'vehicleInfo.partsInfo.options', []);

  const { packageValue, displayedOptions, updatedOptionsCount } = useMemo(() => {
    const updatedOptions = options.map(option => ({
      ...option,
      price: getOptionPrice(get(optionPrices, option.id, {})),
    }));

    return {
      packageValue: sumBy(updatedOptions, 'price'),
      displayedOptions: orderBy(updatedOptions, ['price'], ['desc']).slice(0, OPTIONS_COLLAPSE_NUMBER),
      updatedOptionsCount: updatedOptions.length,
    };
  }, [optionPrices, options]);
  const isNewType = isNew(vinData.type);
  const [isItemOpened, setIsItemOpened] = useState(!!isExpanded);

  const handleToggle = useCallback(
    event => {
      setIsItemOpened(!isItemOpened);
      event.preventDefault();
    },
    [isItemOpened]
  );

  return (
    <Row
      className={classnames('installed-upgrades mx-0 px-0 pos-r')}
      data-tracking-parent="edm-entry-options-and-packages"
    >
      <details open={isItemOpened} className="w-100 mb-1_5">
        <summary
          className="d-flex justify-content-between py-1 c-pointer"
          data-tracking-id="vdp_view_content"
          data-subaction-name={TrackingConstant.VIEW_FEATURES}
          data-tracking-value="Installed Upgrades"
          onClick={handleToggle}
          data-no-refresh
        >
          <SummaryHeadingTag className="feature-title fw-bold m-0 size-16 d-flex align-items-start">
            <i className="icon-star-full me-1 flex-shrink-0" aria-hidden />
            <span>
              {updatedOptionsCount} {getPlural('Installed Upgrade', updatedOptionsCount)}
              {packageValue > 0 && (
                <span className="medium fw-medium fst-italic text-green-30">
                  {' '}
                  {isNewType ? 'Valued at' : 'Originally valued at'} {formatPriceStringV2(packageValue)}
                </span>
              )}
            </span>
          </SummaryHeadingTag>
          <i
            className={classnames('medium text-primary-darker', {
              'icon-arrow-down4': !isItemOpened,
              'icon-arrow-up4': isItemOpened,
            })}
            aria-hidden
          />
        </summary>
        <ul className="m-0 list-unstyled">
          {packageValue > 0 && (
            <li className="upgrade-item pb-1 d-flex justify-content-between">
              <div className="d-flex flex-column">
                <span className="fw-medium">{isNewType ? 'Total value' : 'Total Original Value'}</span>
                {isNewType && <span>(included in price)</span>}
              </div>
              <div className="text-green-50 fw-bold">{formatPriceStringV2(packageValue)}</div>
            </li>
          )}
          {displayedOptions.map(({ name, price, id }, index) => (
            <UpgradeItem
              key={id}
              vin={vinData.vin}
              name={name}
              price={price}
              id={id}
              packageValue={packageValue}
              isDescriptionEligible={isDescriptionEnabled({ index, optionsCount: displayedOptions.length })}
            />
          ))}
        </ul>
        <button
          className="gradient pos-a left-0 right-0"
          onClick={toggleModal}
          data-tracking-id="vdp_view_content"
          data-subaction-name={TrackingConstant.VIEW_FEATURES}
          tabIndex={-1}
          aria-hidden
        />
      </details>
    </Row>
  );
}

InstalledUpgradesUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  optionPrices: PropTypes.objectOf(
    PropTypes.shape({
      baseMSRP: PropTypes.number,
    })
  ),
  isExpanded: PropTypes.bool,
  toggleModal: PropTypes.func,
  summaryHeadingTag: PropTypes.string,
};

export const stateToPropsConfig = {
  optionPrices: bindToPath(({ vinData: { vin } }) => `optionPrices["${vin}"]`, InventoryModel),
};

export const InstalledUpgrades = connectToModel(InstalledUpgradesUI, stateToPropsConfig);
